import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from '@phosphor-icons/react';
import axios from 'axios';

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_nsec-refresh/artifacts/nnzc6d20_IMG_1445.JPG";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Defaults are used only as a final fallback if the API can't be reached
// at all — otherwise we wait for the live CMS content rather than briefly
// flashing these on first paint.
const DEFAULTS = {
  overline: "North Somerset Environment Company",
  heading: "Service Provision & Environmental Services across the",
  heading_highlight: "South West",
  heading_suffix: "",
  description: "Professional, reliable, and sustainable service solutions. From environmental services and commercial collections to highways and driver training, we're your local partner in protecting the environment.",
  cta_primary: "Get a Free Quote",
  cta_secondary: "Our Services",
  background_image: HERO_IMAGE,
  stats: [
    { value: "6", label: "Core Services" },
    { value: "100+", label: "Business Clients" },
    { value: "24/7", label: "Support Available" },
    { value: "100%", label: "Local Team" }
  ]
};

export const HeroSection = () => {
  // Start with `null` so the text doesn't render at all until we know
  // whether the CMS has overrides — this kills the "default text flashes
  // for a frame, then snaps to admin-edited copy" experience.
  const [c, setC] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/page-content/hero`)
      .then(r => {
        if (cancelled) return;
        const live = r.data && Object.keys(r.data).length > 0 ? r.data : {};
        setC({ ...DEFAULTS, ...live });
      })
      .catch(() => { if (!cancelled) setC(DEFAULTS); });
    return () => { cancelled = true; };
  }, []);

  // While loading we still paint the dark background + image immediately
  // (no layout shift, no white flash), but hold the text behind opacity:0
  // and fade everything in as one piece once the live content arrives.
  const ready = c !== null;
  const view = c || DEFAULTS;

  return (
    <section className="relative min-h-screen flex items-center" data-testid="hero-section">
      <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${view.background_image || HERO_IMAGE})` }} />
      <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/90 via-neutral-900/80 to-neutral-900/60" />
      <div className={`relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20 transition-opacity duration-300 ${ready ? 'opacity-100' : 'opacity-0'}`}>
        <div className="max-w-3xl">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={ready ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }} transition={{ duration: 0.6 }} className="accent-font text-lime-400 mb-6" data-testid="hero-overline">
            {view.overline}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={ready ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }} transition={{ duration: 0.6, delay: 0.1 }} className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.1] mb-8" data-testid="hero-heading">
            {view.heading}{' '}<span className="text-lime-400">{view.heading_highlight}</span> {view.heading_suffix}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={ready ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }} transition={{ duration: 0.6, delay: 0.2 }} className="text-lg lg:text-xl text-neutral-300 leading-relaxed mb-10 max-w-2xl" data-testid="hero-description">
            {view.description}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={ready ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }} transition={{ duration: 0.6, delay: 0.3 }} className="flex flex-wrap gap-4">
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="hero-cta-primary">
              {view.cta_primary} <ArrowRight size={20} weight="bold" />
            </Link>
            <Link to="/services" className="btn-secondary-light" data-testid="hero-cta-secondary">
              {view.cta_secondary}
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={ready ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }} transition={{ duration: 0.6, delay: 0.4 }} className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 pt-8 border-t border-white/20">
            {(view.stats || []).map((stat, index) => (
              <div key={index} data-testid={`hero-stat-${index}`}>
                <p className="font-outfit font-bold text-3xl lg:text-4xl text-lime-400 mb-1">{stat.value}</p>
                <p className="text-neutral-400 text-sm">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
